/* eslint-disable react/prop-types */
import { Radio, Select } from '@tmov/design-system';
import { DatePicker, Form } from 'antd';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import NumberFormat from '../../../../components/NumberFormat';
import TipoCadenciaEnum from '../../../../Enum/TipoCadenciaEnum';
import { carregaCidades } from '../../../../helpers/cidades';
import { searchEspeciesByProduto } from '../../../../helpers/especies';
import {
  formatarCnpjCpfIMask,
  limitarLabel,
  removeThousandSeparatorMask,
  toTitleCase,
} from '../../../../helpers/formatador';
import { getPagadores } from '../../../../helpers/pagadores';
import { searchProdutosByClientePagador } from '../../../../helpers/produtos';
import { useAuth } from '../../../../security/authContext';
import FormSection from '../FormSection';
import { SectionInputsRow } from '../FormSection/styles';

const cadenciaOptions = TipoCadenciaEnum.getValues().map(tipoCadencia => ({
  id: tipoCadencia.value.toString(),
  name: tipoCadencia.label,
  description: tipoCadencia.desc,
}));

function DadosGerais({ setFieldsValue, getFieldsValue }) {
  const { user } = useAuth();

  const [clientePagadorOptions, setClientePagadorOptions] = useState([]);
  const [clientePagadorLoading, setClientePagadorLoading] = useState(false);
  const [idClientePagador, setIdClientePagador] = useState();
  const [idProduto, setIdProduto] = useState();

  const [produtoValues, setProdutoValues] = useState([]);
  const [produtoLoading, setProdutoLoading] = useState(false);
  const produtoOptions = useMemo(
    () =>
      produtoValues?.length > 0
        ? produtoValues.map(item => ({
            id: item.IdProduto,
            value: toTitleCase(item.Descricao),
          }))
        : [],
    [produtoValues]
  );

  const [especieValues, setEspecieValues] = useState([]);
  const [especieLoading, setEspecieLoading] = useState(false);
  const especieOptions = useMemo(
    () =>
      especieValues?.length > 0
        ? especieValues.map(item => ({
            id: item.IdEspecie,
            value: toTitleCase(item.Descricao),
          }))
        : [],
    [especieValues]
  );

  const [cidadeOrigemSearch, setCidadeOrigemSearch] = useState('');
  const [cidadeOrigemValues, setCidadeOrigemValues] = useState([]);
  const [cidadeOrigemLoading, setCidadeOrigemLoading] = useState(false);
  const cidadeOrigemOptions = useMemo(
    () =>
      cidadeOrigemValues?.length > 0
        ? cidadeOrigemValues.map(item => ({
            id: item.idCidade,
            value: `${toTitleCase(item.cidadeNome)} - ${item.sigla}`,
          }))
        : [],
    [cidadeOrigemValues]
  );
  const handleChangeCidadeOrigemSearch = useDebouncedCallback(searchValue => {
    setCidadeOrigemSearch(searchValue);
  }, 300);

  const [cidadeDestinoSearch, setCidadeDestinoSearch] = useState('');
  const [cidadeDestinoValues, setCidadeDestinoValues] = useState([]);
  const [cidadeDestinoLoading, setCidadeDestinoLoading] = useState(false);
  const cidadeDestinoOptions = useMemo(
    () =>
      cidadeDestinoValues?.length > 0
        ? cidadeDestinoValues.map(item => ({
            id: item.idCidade,
            value: `${toTitleCase(item.cidadeNome)} - ${item.sigla}`,
          }))
        : [],
    [cidadeDestinoValues]
  );
  const handleChangeCidadeDestinoSearch = useDebouncedCallback(searchValue => {
    setCidadeDestinoSearch(searchValue);
  }, 300);

  useEffect(() => {
    const { produto, clientePagador } = getFieldsValue(true);
    setIdClientePagador(clientePagador?.id);
    setIdProduto(produto?.id);
  }, [getFieldsValue]);

  const handleClientepagador = value => {
    setIdClientePagador(value?.id);
    setIdProduto(null);

    setFieldsValue({
      especie: undefined,
      produto: undefined,
    });
  };

  const validateNumber = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Insira um valor'));
    }

    if (typeof removeThousandSeparatorMask(value) !== 'number') {
      return Promise.reject(new Error('Selecione um valor numérico'));
    }

    return Promise.resolve();
  };

  const validatePeriodo = async (value, getFieldValue) => {
    if (!value) {
      return Promise.reject(new Error('Insira uma data'));
    }

    const inicio = getFieldValue('inicioCarregamento');
    const fim = getFieldValue('fimCarregamento');

    if (inicio && fim && inicio.isAfter(fim)) {
      return Promise.reject(
        new Error('A data de início da cotação deve ser menor que a data fim ')
      );
    }
    return Promise.resolve();
  };

  useEffect(() => {
    const carregaPagadores = async () => {
      setClientePagadorLoading(true);
      const pagadores = await getPagadores(user);
      setClientePagadorOptions(
        pagadores.map(item => ({
          id: item.id,
          value: `${limitarLabel(
            toTitleCase(item.razao_social),
            20
          )} - ${formatarCnpjCpfIMask(
            item.cnpj
          )} ${item.cidade.toUpperCase()} (${item.uf.toUpperCase()})
          ${
            item.id_cliente_endereco
              ? `- (${item.bairro_cliente_endereco})`
              : null
          }`,
        }))
      );
      setClientePagadorLoading(false);
    };
    carregaPagadores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const carregaProdutos = async () => {
      if (!idClientePagador) {
        return;
      }

      await searchProdutosByClientePagador(
        idClientePagador,
        setProdutoValues,
        setProdutoLoading
      );
    };
    carregaProdutos();
  }, [idClientePagador]);

  useEffect(() => {
    const carregaEspecies = async () => {
      if (!idClientePagador || !idProduto) {
        return;
      }

      const especies = await searchEspeciesByProduto(
        idClientePagador,
        idProduto,
        setEspecieValues,
        setEspecieLoading
      );

      if (especies.length === 1) {
        setFieldsValue({
          especie: {
            id: especies[0].IdEspecie,
            value: toTitleCase(especies[0].Descricao),
          },
        });
      }
    };
    carregaEspecies();
  }, [idClientePagador, idProduto, setFieldsValue]);

  useEffect(() => {
    carregaCidades(
      cidadeOrigemSearch,
      setCidadeOrigemValues,
      setCidadeOrigemLoading
    );
  }, [cidadeOrigemSearch]);

  useEffect(() => {
    carregaCidades(
      cidadeDestinoSearch,
      setCidadeDestinoValues,
      setCidadeDestinoLoading
    );
  }, [cidadeDestinoSearch]);

  return (
    <>
      <FormSection name="Tipo de cadência">
        <Form.Item
          name="tipoCadencia"
          initialValue="1"
          valuePropName="selectedOptionId"
        >
          <Radio options={cadenciaOptions} />
        </Form.Item>
      </FormSection>
      <FormSection
        name="Dados do carregamento"
        description="Informe os dados do carregamento que será cotado."
      >
        <Form.Item
          name="clientePagador"
          valuePropName="selectedOption"
          trigger="onChangeOption"
          validateTrigger="onChangeOption"
          rules={[
            {
              required: true,
              message: 'Insira um cliente pagador',
            },
          ]}
          wrapperCol={{ style: { gap: 12 } }}
        >
          <Select
            data-testid="clientepagador-select"
            id="clientePagador"
            label="Cliente pagador"
            options={clientePagadorOptions}
            onChangeOption={handleClientepagador}
            isLoading={clientePagadorLoading}
            width="476px"
            noResultsText="Não há itens que correspondem à pesquisa"
          />
        </Form.Item>
        <SectionInputsRow>
          <Form.Item
            name="produto"
            valuePropName="selectedOption"
            trigger="onChangeOption"
            validateTrigger="onChangeOption"
            rules={[
              {
                required: true,
                message: 'Insira um produto',
              },
            ]}
            wrapperCol={{ style: { gap: 12 } }}
          >
            <Select
              data-testid="produto-select"
              id="produto"
              label="Produto"
              options={produtoOptions}
              onChangeOption={value => setIdProduto(value?.id)}
              isLoading={produtoLoading}
              width="320px"
              noResultsText="Não há itens que correspondem à pesquisa"
            />
          </Form.Item>
          <Form.Item
            name="especie"
            valuePropName="selectedOption"
            trigger="onChangeOption"
            validateTrigger="onChangeOption"
            rules={[
              {
                required: true,
                message: 'Insira uma espécie',
              },
            ]}
            wrapperCol={{ style: { gap: 12 } }}
          >
            <Select
              data-testid="especie-select"
              id="especie"
              label="Espécie"
              options={especieOptions}
              isLoading={especieLoading}
              width="320px"
              noResultsText="Não há itens que correspondem à pesquisa"
            />
          </Form.Item>
        </SectionInputsRow>
        <SectionInputsRow>
          <Form.Item name="volume" rules={[{ validator: validateNumber }]}>
            <NumberFormat name="volume" label="Volume (ton)" />
          </Form.Item>
          <Form.Item name="cadencia" rules={[{ validator: validateNumber }]}>
            <NumberFormat name="cadencia" label="Cadencia (ton)" />
          </Form.Item>
        </SectionInputsRow>
        <SectionInputsRow>
          <Form.Item
            name="cidadeOrigem"
            valuePropName="selectedOption"
            trigger="onChangeOption"
            validateTrigger="onChangeOption"
            rules={[
              {
                required: true,
                message: 'Insira uma cidade',
              },
            ]}
            wrapperCol={{ style: { gap: 12 } }}
          >
            <Select
              data-testid="cidadeOrigem-select"
              id="cidadeOrigem"
              label="Cidade de origem"
              onChangeSearch={handleChangeCidadeOrigemSearch}
              options={cidadeOrigemOptions}
              isLoading={cidadeOrigemLoading}
              width="320px"
              noResultsText={
                cidadeOrigemSearch.length < 3
                  ? 'Insira pelo menos 3 caracteres para pesquisar'
                  : 'Não há itens que correspondem à pesquisa'
              }
            />
          </Form.Item>
          <Form.Item
            name="cidadeDestino"
            valuePropName="selectedOption"
            trigger="onChangeOption"
            validateTrigger="onChangeOption"
            rules={[
              {
                required: true,
                message: 'Insira uma cidade',
              },
            ]}
            wrapperCol={{ style: { gap: 12 } }}
          >
            <Select
              data-testid="cidadeDestino-select"
              id="cidadeDestino"
              label="Cidade de destino"
              onChangeSearch={handleChangeCidadeDestinoSearch}
              options={cidadeDestinoOptions}
              isLoading={cidadeDestinoLoading}
              width="320px"
              noResultsText={
                cidadeDestinoSearch.length < 3
                  ? 'Insira pelo menos 3 caracteres para pesquisar'
                  : 'Não há itens que correspondem à pesquisa'
              }
            />
          </Form.Item>
        </SectionInputsRow>
      </FormSection>
      <FormSection
        name="Período"
        description="Informe o início e fim do carregamento."
        isLast
      >
        <SectionInputsRow>
          <Form.Item
            style={{ maxWidth: 150 }}
            name="inicioCarregamento"
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => validatePeriodo(value, getFieldValue),
              }),
            ]}
          >
            <DatePicker
              data-testid="inicioCarregamentoInput"
              placeholder="Início"
              disabledDate={d => !d || d.isBefore(moment().add(-1, 'days'))}
              format="DD/MM/YYYY"
            />
          </Form.Item>
          <Form.Item
            style={{ maxWidth: 150 }}
            name="fimCarregamento"
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value) => validatePeriodo(value, getFieldValue),
              }),
            ]}
          >
            <DatePicker
              data-testid="fimCarregamentoInput"
              placeholder="Fim"
              disabledDate={d => !d || d.isBefore(moment().add(-1, 'days'))}
              format="DD/MM/yyyy"
            />
          </Form.Item>
        </SectionInputsRow>
      </FormSection>
    </>
  );
}

export default DadosGerais;
